import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Check, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function ServiceTypesTab() {
  const { activeOrgId } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try { const { data } = await api.get(`/organizations/${activeOrgId}/service-types`); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const create = async () => {
    if (!newName.trim()) return toast.error("Name is required");
    setAdding(true);
    try {
      await api.post(`/organizations/${activeOrgId}/service-types`, { name: newName.trim() });
      setNewName(""); toast.success("Service type added"); fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const saveEdit = async (it) => {
    if (!editName.trim()) return toast.error("Name is required");
    try {
      await api.patch(`/organizations/${activeOrgId}/service-types/${it.id}`, { name: editName.trim() });
      setEditingId(null); setEditName(""); toast.success("Saved"); fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/service-types/${removeTarget.id}`);
      toast.success("Removed"); setRemoveTarget(null); fetchAll();
    } catch (e) { toast.error(formatApiError(e)); setRemoveTarget(null); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
        <div className="px-7 py-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-serif text-2xl">Service Types</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Define the kinds of services your church runs — these appear in the mobile portal's count form and service log.</p>
          </div>
        </div>

        <div className="px-7 py-5 border-b border-border">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Add a new service type</Label>
          <div className="flex gap-2 mt-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") create(); }}
              placeholder="e.g. Tuesday Bible Study"
              className="rounded-xl h-11"
              data-testid="service-type-new-input"
            />
            <Button onClick={create} disabled={adding} className="rounded-xl h-11 px-5 soft-glow" data-testid="service-type-add-button">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {loading ? (
            <div className="px-7 py-8 text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-7 py-12 text-center">
              <CalendarDays className="w-7 h-7 mx-auto text-muted-foreground" />
              <div className="font-serif text-lg mt-3">No service types yet</div>
              <p className="text-sm text-muted-foreground mt-1">Add your first one above.</p>
            </div>
          ) : items.map((it) => (
            <div key={it.id} className="px-7 py-3.5 flex items-center gap-3" data-testid={`service-type-row-${it.id}`}>
              {editingId === it.id ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(it); if (e.key === "Escape") { setEditingId(null); setEditName(""); } }}
                    className="rounded-xl h-10 flex-1"
                    autoFocus
                    data-testid={`service-type-edit-input-${it.id}`}
                  />
                  <Button size="icon" variant="ghost" className="rounded-xl" onClick={() => saveEdit(it)} data-testid={`service-type-save-${it.id}`}><Check className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="rounded-xl" onClick={() => { setEditingId(null); setEditName(""); }}><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="font-medium truncate">{it.name}</span>
                    {it.is_default && <Badge variant="outline" className="text-[10px]">Default</Badge>}
                    <span className="text-[11px] text-muted-foreground font-mono truncate">{it.key}</span>
                  </div>
                  <Button size="icon" variant="ghost" className="rounded-xl" onClick={() => { setEditingId(it.id); setEditName(it.name); }} data-testid={`service-type-edit-${it.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={() => setRemoveTarget(it)} data-testid={`service-type-delete-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title={`Delete "${removeTarget?.name}"?`}
        description="If any service logs still use this type, deletion will be blocked. Past services keep working — only new logs are affected."
        confirmLabel="Delete"
        onConfirm={doDelete}
        testid="confirm-delete-service-type"
      />
    </div>
  );
}
